import clsx from 'clsx';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import Icon, { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import useConfirm from '@/src/components/common/Modal/ConfirmModal/useConfirm';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import {
  EmailEntryToUpdateStatus,
  InboxEntryStatus,
  CloseReasonCode,
} from '@/src/components/project/Inbox2/types';
import { RootState } from '@/src/reducers/types';

import StatusDropdown from '../../../Common/StatusDropdown/StatusDropdown';
import useSyncInboxEntry from '../../../useSyncInboxEntry';
import { useUpdateEmailStatus } from '../../api/updateEmailStatus';

const checkShouldShowClosePrompt = (
  newStatus: InboxEntryStatus,
  emailEntry: EmailEntryToUpdateStatus,
  projectRequired: boolean
) => {
  if (newStatus !== InboxEntryStatus.Closed) {
    return false;
  }

  // @TODO: to move prop to BE?
  // const hasFiles = emailEntry.files.length > 0;
  // const hasTasks = emailEntry.tasks.length > 0;

  // return projectRequired && !emailEntry.isMisc && !hasFiles && !hasTasks;

  return false;
};

// export const checkEmailHasRequiredAction = (emailEntry: EmailEntryToUpdateStatus) => {
//   if (emailEntry.isMisc) {
//     return false;
//   }

//   const hasFiles = emailEntry.files.length > 0;

//   if (!hasFiles) {
//     return false;
//   }

//   const isAllFilesClosed =
//     emailEntry.files.filter((ee) => ee.file.status === InboxEntryStatus.Closed).length ===
//     emailEntry.files.length;

//   return isAllFilesClosed;
// };

const getTooltip = (emailEntry: EmailEntryToUpdateStatus, isDisabled: boolean) => {
  if (!isDisabled) {
    return null;
  }

  if (emailEntry.status === InboxEntryStatus.Closed) {
    return null;
  }

  const tooltipId = `tufs_${emailEntry.id}`;
  const content =
    emailEntry.allowClose.reasonCode === CloseReasonCode.ATTACHMENTS_NOT_CLOSED
      ? 'Please ensure that all file attachments have been completed first.'
      : 'Requires action to close entry.';
  // const content =
  //   emailEntry.files.filter((ef) => ef.file.status !== InboxEntryStatus.Closed).length > 0
  //     ? 'Please ensure that all file attachments have been completed first.'
  //     : 'Requires action to close entry.';

  return {
    id: tooltipId,
    component: (
      <Tooltip className="" id={tooltipId} place="bottom" effect="solid">
        <span>
          <Icon
            icon={ICONS.info}
            iconType={ICON_TYPES.outlined}
            className={clsx('tw-mr-[5px]', 'tw-text-white tw-text-[18px]')}
          />
          {content}
        </span>
      </Tooltip>
    ),
  };
};

const checkDisabled = ({
  emailEntry,
  projectRequired,
}: {
  emailEntry: EmailEntryToUpdateStatus;
  projectRequired: boolean;
}) => {
  if (!projectRequired) {
    return false;
  }

  if (emailEntry.status === InboxEntryStatus.Closed) {
    return false;
  }

  if (emailEntry.allowClose.allowed === false) {
    return true;
  }

  return emailEntry.allowedStatusChanges ? emailEntry.allowedStatusChanges.length < 1 : true;
};

type UpdateEmailStatusProps = {
  emailEntry: EmailEntryToUpdateStatus;
};

const UpdateEmailStatus = ({ emailEntry }: UpdateEmailStatusProps) => {
  const [ConfirmCloseModal, confirmClose] = useConfirm({
    classes: '',
    // modalWidth: '600px',
    title: 'No action taken. Close Entry',
    message: `No further action is required on this entry. Please confirm to proceed.`,
    cancelBtn: {
      label: 'Cancel',
    },
    confirmBtn: {
      label: 'Proceed',
    },
  });
  // const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const project = useSelector((store: RootState) => store.project.Selected);
  const { projectId } = useParams<{ projectId: string }>();
  const updateEmailStatusMutation = useUpdateEmailStatus({
    projectId,
    emailId: emailEntry.id,
    inboxEntryId: emailEntry.inboxEntryId,
  });
  const isDisabled = checkDisabled({
    projectRequired: project.requiredAction,
    emailEntry,
  });

  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const isReadOnly = loggedUser.data.userRole >= 5;

  const tooltip = !isReadOnly && getTooltip(emailEntry, isDisabled);
  const syncInboxEntry = useSyncInboxEntry();

  const handleSelectOption = useCallback(
    async (newStatus) => {
      const newStatusValue = newStatus.value as InboxEntryStatus;
      const shouldShowClosePrompt = checkShouldShowClosePrompt(
        newStatusValue,
        emailEntry,
        project.requiredAction
      );

      if (shouldShowClosePrompt) {
        if (!(await confirmClose())) {
          return;
        }
      }

      const response = await updateEmailStatusMutation.mutateAsync({
        projectId,
        inboxEntryId: emailEntry.inboxEntryId,
        emailId: emailEntry.id,
        status: newStatusValue,
      });

      if (isResponseError(response)) {
        return;
      }

      syncInboxEntry(response.data['inbox-entry']);
      return response?.data;
    },
    [
      projectId,
      emailEntry,
      updateEmailStatusMutation,
      confirmClose,
      project.requiredAction,
      syncInboxEntry,
    ]
  );

  if (!emailEntry.status) {
    return null;
  }

  return (
    <React.Fragment>
      {updateEmailStatusMutation.isLoading && <Spinner />}
      <StatusDropdown
        value={emailEntry.status}
        isDisabled={isDisabled || isReadOnly}
        isDuplicate={emailEntry.duplicate}
        // onSelect={handleSelect}
        onSelectOption={handleSelectOption}
        tooltip={tooltip}
      />
      <ConfirmCloseModal />
    </React.Fragment>
  );
};

export default UpdateEmailStatus;
